export const getKeycloakRealm = () =>
  getRequiredEnvVariable('VITE_KEYCLOAK_REALM', 'KEYCLOAK_REALM')

export const getKeycloakUrl = () => getRequiredEnvVariable('VITE_KEYCLOAK_URL', 'KEYCLOAK_URL')

export const getSentryDsn = () => getRequiredEnvVariable('VITE_SENTRY_DSN', 'SENTRY_DSN')

export const getSentryEnvironment = () =>
  getRequiredEnvVariable('VITE_SENTRY_ENVIRONMENT', 'SENTRY_ENVIRONMENT')

export const getKeycloakClientId = () =>
  getRequiredEnvVariable('VITE_KEYCLOAK_CLIENT_ID', 'KEYCLOAK_CLIENT_ID')

export const isMaintenanceModeOn = () =>
  getRequiredEnvVariable('VITE_MAINTENANCE_MODE', 'MAINTENANCE_MODE') === 'true'

export const isWebexMultistreamEnabled = () =>
  getRequiredEnvVariable('VITE_WEBEX_MULTISTREAM', 'WEBEX_MULTISTREAM', 'false') === 'true'

export const getMaintenanceModeBypassCode = () =>
  getRequiredEnvVariable('VITE_MAINTENANCE_MODE_BYPASS_CODE', 'MAINTENANCE_MODE_BYPASS_CODE')

export const getRefreshIntervalIncomingRequests = () =>
  secondsStringToMs(
    getRequiredEnvVariable(
      'VITE_REFRESH_INTERVAL_INCOMING_REQUESTS',
      'REFRESH_INTERVAL_INCOMING_REQUESTS',
      '10'
    )
  )

export const getRefreshIntervalOutgoingRequests = () =>
  secondsStringToMs(
    getRequiredEnvVariable(
      'VITE_REFRESH_INTERVAL_OUTGOING_REQUESTS',
      'REFRESH_INTERVAL_OUTGOING_REQUESTS',
      '60'
    )
  )

export const getRefreshIntervalCases = () =>
  secondsStringToMs(
    getRequiredEnvVariable('VITE_REFRESH_INTERVAL_CASES', 'REFRESH_INTERVAL_CASES', '60')
  )

export const getRefreshIntervalPatientDetails = () =>
  secondsStringToMs(
    getRequiredEnvVariable(
      'VITE_REFRESH_INTERVAL_PATIENT_DETAILS',
      'REFRESH_INTERVAL_PATIENT_DETAILS',
      '60'
    )
  )

export const getRefreshIntervalVitalSigns = () =>
  secondsStringToMs(
    getRequiredEnvVariable(
      'VITE_REFRESH_INTERVAL_VITAL_SIGNS',
      'REFRESH_INTERVAL_VITAL_SIGNS',
      '30'
    )
  )

export const getRefreshIntervalNotificationCenter = () =>
  secondsStringToMs(
    getRequiredEnvVariable(
      'VITE_REFRESH_INTERVAL_NOTIFICATION_CENTER',
      'REFRESH_INTERVAL_NOTIFICATION_CENTER',
      '10'
    )
  )

export const getAkBaseUrl = () => getRequiredEnvVariable('VITE_AK_BASE_URL', 'AK_BASE_URL')

export const getRefreshIntervalCheckLoginStatus = () =>
  secondsStringToMs(
    getRequiredEnvVariable(
      'VITE_REFRESH_INTERVAL_CHECK_LOGIN_STATUS',
      'REFRESH_INTERVAL_CHECK_LOGIN_STATUS',
      '30'
    )
  )

export const getRefreshIntervalShiftDuration = () =>
  secondsStringToMs(
    getRequiredEnvVariable(
      'VITE_REFRESH_INTERVAL_SHIFT_DURATION',
      'REFRESH_INTERVAL_SHIFT_DURATION',
      '30'
    )
  )

export const getConsultationActivityHeartbeatInterval = () =>
  secondsStringToMs(
    getRequiredEnvVariable(
      'VITE_CONSULTATION_ACTIVITY_HEARTBEAT_INTERVAL',
      'VITE_CONSULTATION_ACTIVITY_HEARTBEAT_INTERVAL',
      '10'
    )
  )

export const getRequiredEdsLimitationsVersion = () =>
  getRequiredEnvVariable(
    'VITE_REQUIRED_EDS_LIMITATIONS_VERSION',
    'REQUIRED_EDS_LIMITATIONS_VERSION'
  )

export const getRequiredEdsLimitationsScope = () =>
  getRequiredEnvVariable('VITE_REQUIRED_EDS_LIMITATIONS_SCOPE', 'REQUIRED_EDS_LIMITATIONS_SCOPE')

export const getRequiredAkiLimitationsVersion = () =>
  getRequiredEnvVariable(
    'VITE_REQUIRED_AKI_LIMITATIONS_VERSION',
    'REQUIRED_AKI_LIMITATIONS_VERSION'
  )

export const getRequiredAkiLimitationsScope = () =>
  getRequiredEnvVariable('VITE_REQUIRED_AKI_LIMITATIONS_SCOPE', 'REQUIRED_AKI_LIMITATIONS_SCOPE')

// Comma-separated tenant ids. Analytics is collected only for users whose JWT
// client_id claim appears here, so an unset list means nothing is collected anywhere.
export const getPosthogEnabledClientIds = () =>
  parseClientIdList(getRuntimeOnlyEnvVariable('POSTHOG_ENABLED_CLIENT_IDS', ''))

export const getPosthogApiKey = () => getRuntimeOnlyEnvVariable('POSTHOG_API_KEY', '')

// Keys the user pseudonym. Must be identical on every deployment that reports into the
// same PostHog project, or one physician becomes a different person per deployment.
export const getPosthogPseudonymPepper = () =>
  getRuntimeOnlyEnvVariable('POSTHOG_PSEUDONYM_PEPPER', '')

export const getPosthogHost = () =>
  getRuntimeOnlyEnvVariable('POSTHOG_HOST', 'https://eu.i.posthog.com')

export const getPosthogEnvironmentTier = () =>
  asSlug(getRuntimeOnlyEnvVariable('POSTHOG_ENVIRONMENT_TIER', UNKNOWN_DEPLOYMENT_LABEL))

export const getPosthogDeploymentId = () =>
  asSlug(getRuntimeOnlyEnvVariable('POSTHOG_DEPLOYMENT_ID', UNKNOWN_DEPLOYMENT_LABEL))

function secondsStringToMs(seconds: string): number {
  return parseInt(seconds, 10) * 1000
}

function parseClientIdList(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.length > 0)
}

const UNKNOWN_DEPLOYMENT_LABEL = 'unknown'
const INVALID_DEPLOYMENT_LABEL = 'invalid'
const SLUG_PATTERN = /^[a-z0-9-]{1,40}$/

// Misconfigured labels become 'invalid' rather than being passed through or dropped,
// so a bad value shows up as its own bucket in the analytics instead of silently
// merging into another environment or disappearing.
function asSlug(value: string): string {
  return SLUG_PATTERN.test(value) ? value : INVALID_DEPLOYMENT_LABEL
}

// Deliberately does NOT consult import.meta.env, unlike getRequiredEnvVariable.
// Vite inlines VITE_* at build time and the shared helper reads it first, so a
// stray .env on a build machine would bake these values into the bundle, where
// deployment-time configuration can no longer change them.
function getRuntimeOnlyEnvVariable(envKey: string, fallback: string): string {
  if (typeof env !== 'undefined' && env[envKey]) {
    return env[envKey]
  }
  return fallback
}

function getRequiredEnvVariable(viteKey: string, envKey: string, fallback?: string): string {
  const localValue = import.meta.env?.[viteKey]
  if (localValue !== undefined) {
    return localValue
  }
  if (typeof env !== 'undefined' && env[envKey] !== undefined) {
    return env[envKey]
  }
  if (fallback !== undefined) {
    return fallback
  }
  throw new Error(`Missing required environment variable: ${viteKey} / ${envKey}`)
}
